import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";
import { ReactComponent as ChevronDownIcon } from "../../assets/icons/caret-down.svg";
import { ReactComponent as CheckMarkIcon } from "../../assets/icons/check-mark.svg";
import Button from "../../components/Button";
import { Page } from "../../components/Page";
import { ShowToast } from "../../components/toast";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { api } from "../../helpers/api";
import { useCrmEnabled } from "../../helpers/hooks/use-crm-enabled";
import { CustomerNotesContent } from "../customer-notes/CustomerNotes";
import { KnowledgeBaseContent } from "../knowledge-base/KnowledgeBase";

// =============================================================================
// TYPES
// =============================================================================

type TicketStatus =
  | "received"
  | "classifying"
  | "classified"
  | "ai_processing"
  | "ai_complete"
  | "pending_review"
  | "approved"
  | "rejected"
  | "escalated"
  | "failed";

type TicketCategory =
  | "pricing_question"
  | "performance_inquiry"
  | "competitor_question"
  | "bug_report"
  | "pms_issue"
  | "billing_question"
  | "onboarding_help"
  | "feature_request"
  | "appreciation"
  | "informative"
  | "cancellation_request"
  | "upgrade_inquiry"
  | "general_inquiry";

type TicketUrgency = "low" | "medium" | "high" | "critical";
type TicketDepartment = "customer_success" | "engineering" | "sales";

interface CsTicket {
  id: string;
  emailFrom: string;
  emailSubject: string;
  emailBody: string;
  emailMessageId: string;
  emailReceivedAt: string;
  userId: string | null;
  hotelName: string | null;
  customerName: string | null;
  category: TicketCategory;
  urgency: TicketUrgency;
  department: TicketDepartment;
  summary: string | null;
  extractedQuestion: string | null;
  requiresAiResponse: boolean;
  churnRisk: boolean;
  needsHumanEscalation: boolean;
  sentimentTone: string | null;
  asanaTaskId: string | null;
  asanaTaskUrl: string | null;
  aiRawResponse: string | null;
  aiResponseGeneratedAt: string | null;
  polishedResponse: string | null;
  finalResponse: string | null;
  status: TicketStatus;
  failedStep: string | null;
  failedReason: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  senderName: string | null;
  senderHotelName: string | null;
  nextSteps: string | null;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const statusColors: Record<TicketStatus, string> = {
  received: "bg-surface-secondary text-gray-800 border-border-primary",
  classifying: "bg-blue-50 text-blue-700 border-blue-200",
  classified: "bg-blue-100 text-blue-800 border-blue-300",
  ai_processing: "bg-purple-100 text-purple-800 border-purple-300",
  ai_complete: "bg-indigo-100 text-indigo-800 border-indigo-300",
  pending_review: "bg-yellow-100 text-yellow-800 border-yellow-300",
  approved: "bg-green-100 text-green-800 border-green-300",
  rejected: "bg-red-100 text-red-800 border-red-300",
  escalated: "bg-orange-100 text-orange-800 border-orange-300",
  failed: "bg-red-200 text-red-900 border-red-400",
};

const statusLabels: Record<TicketStatus, string> = {
  received: "Received",
  classifying: "Classifying",
  classified: "Classified",
  ai_processing: "AI Processing",
  ai_complete: "AI Complete",
  pending_review: "Pending Review",
  approved: "Approved",
  rejected: "Rejected",
  escalated: "Escalated",
  failed: "Failed",
};

const categoryLabels: Record<TicketCategory, string> = {
  pricing_question: "Pricing",
  performance_inquiry: "Performance",
  competitor_question: "Competitor",
  bug_report: "Bug Report",
  pms_issue: "PMS Issue",
  billing_question: "Billing",
  onboarding_help: "Onboarding",
  feature_request: "Feature Request",
  appreciation: "Appreciation",
  informative: "Informative",
  cancellation_request: "Cancellation",
  upgrade_inquiry: "Upgrade",
  general_inquiry: "General",
};

const urgencyColors: Record<TicketUrgency, string> = {
  low: "bg-surface-secondary text-content-primary border-border-primary",
  medium: "bg-blue-100 text-blue-700 border-blue-300",
  high: "bg-orange-100 text-orange-800 border-orange-300",
  critical: "bg-red-100 text-red-800 border-red-300",
};

const departmentLabels: Record<TicketDepartment, string> = {
  customer_success: "CS",
  engineering: "Engineering",
  sales: "Sales",
};

// =============================================================================
// HELPERS
// =============================================================================

function formatRelativeDate(date: Date): string {
  const now = new Date();
  const et = (d: Date) =>
    new Date(d.toLocaleString("en-US", { timeZone: "America/New_York" }));
  const today = et(now);
  const target = et(date);

  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  const diffDays = Math.floor((today.getTime() - target.getTime()) / 86400000);

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
  });
}

// =============================================================================
// TABS
// =============================================================================

type CsTab = "tickets" | "profiles" | "kb";

const TAB_CONFIG: { key: CsTab; label: string }[] = [
  { key: "tickets", label: "Tickets" },
  { key: "profiles", label: "Customer Profiles" },
  { key: "kb", label: "Knowledge Base" },
];

// =============================================================================
// MAIN COMPONENT — TABBED CONTAINER
// =============================================================================

export function CustomerSuccess() {
  const [activeTab, setActiveTab] = useState<CsTab>("tickets");

  return (
    <Page>
      <div className="p-4 md:h-[calc(100dvh-62px)] overflow-auto lg:h-[100dvh] bg-neutral-200 flex-1">
        {/* Page Title */}
        <h1 className="text-2xl font-semibold text-black mb-4">
          Customer Success
        </h1>

        {/* Tab Bar */}
        <div className="flex items-center gap-1 mb-4 bg-white rounded-lg p-1 shadow-sm border border-[#E9E9EB] w-fit">
          {TAB_CONFIG.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={clsx(
                "px-4 py-2 text-sm font-medium rounded-md transition-colors",
                activeTab === tab.key
                  ? "bg-primary text-white"
                  : "text-gray-600 hover:bg-gray-100",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === "tickets" && <TicketsContent />}
        {activeTab === "profiles" && <CustomerNotesContent />}
        {activeTab === "kb" && <KnowledgeBaseContent />}
      </div>
    </Page>
  );
}

// =============================================================================
// TICKETS TAB CONTENT
// =============================================================================

function TicketsContent() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [urgencyFilter, setUrgencyFilter] = useState<string>("");
  const [departmentFilter, setDepartmentFilter] = useState<string>("");
  const [datePreset, setDatePreset] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [tableData, setTableData] = useState<CsTicket[]>([]);
  const loadMoreRef = useRef<HTMLButtonElement>(null);
  const queryClient = useQueryClient();

  // Dropdown open states
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [urgencyDropdownOpen, setUrgencyDropdownOpen] = useState(false);
  const [departmentDropdownOpen, setDepartmentDropdownOpen] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const urgencyDropdownRef = useRef<HTMLDivElement>(null);
  const departmentDropdownRef = useRef<HTMLDivElement>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target as Node)
      ) {
        setStatusDropdownOpen(false);
      }
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target as Node)
      ) {
        setCategoryDropdownOpen(false);
      }
      if (
        urgencyDropdownRef.current &&
        !urgencyDropdownRef.current.contains(event.target as Node)
      ) {
        setUrgencyDropdownOpen(false);
      }
      if (
        departmentDropdownRef.current &&
        !departmentDropdownRef.current.contains(event.target as Node)
      ) {
        setDepartmentDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const hasActiveFilters =
    search ||
    statusFilter ||
    categoryFilter ||
    urgencyFilter ||
    departmentFilter;

  // Stats query
  const { data: stats } = useQuery({
    queryKey: ["cs-stats"],
    queryFn: () => api.csTickets.stats(),
  });

  // Tickets query
  const fetchTickets = async ({ pageParam }: { pageParam: number }) => {
    const res = await api.csTickets.list({
      page: pageParam,
      limit: 20,
      status: statusFilter || undefined,
      category: categoryFilter || undefined,
      urgency: urgencyFilter || undefined,
      department: departmentFilter || undefined,
      search: debouncedSearch || undefined,
      dateFrom: dateFrom ? dateFrom.toISOString().split("T")[0] : undefined,
      dateTo: dateTo ? dateTo.toISOString().split("T")[0] : undefined,
    });
    return res;
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: [
      "cs-tickets",
      statusFilter,
      categoryFilter,
      urgencyFilter,
      departmentFilter,
      debouncedSearch,
      dateFrom?.toISOString(),
      dateTo?.toISOString(),
    ],
    queryFn: fetchTickets,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages, pageParam) => {
      if (!lastPage.tickets || lastPage.tickets.length < 20) return null;
      return pageParam + 1;
    },
  });

  useMemo(() => {
    if (data) {
      const newData = data.pages.flatMap((page) => page.tickets || []);
      setTableData(newData);
    }
  }, [data]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: null, rootMargin: "20px", threshold: 1.0 },
    );
    if (loadMoreRef.current) observer.observe(loadMoreRef.current);
    return () => {
      if (loadMoreRef.current) observer.unobserve(loadMoreRef.current);
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const columnHelper = createColumnHelper<CsTicket>();

  const columns = [
    columnHelper.accessor("status", {
      header: "Status",
      cell: (info) => (
        <div
          className={`${statusColors[info.getValue()]} text-xs font-medium border rounded py-1 px-2 flex items-center justify-center w-fit whitespace-nowrap`}
        >
          {statusLabels[info.getValue()]}
        </div>
      ),
    }),
    columnHelper.accessor("customerName", {
      header: "Sender",
      cell: (info) => (
        <div className="flex flex-col gap-0.5 max-w-[200px]">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-sm truncate">
              {info.getValue() || info.row.original.emailFrom.split("@")[0]}
            </span>
            {info.row.original.churnRisk && (
              <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-bold bg-red-100 text-red-700 border border-red-300 rounded">
                CHURN
              </span>
            )}
            {info.row.original.needsHumanEscalation && (
              <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-bold bg-orange-100 text-orange-700 border border-orange-300 rounded">
                HUMAN
              </span>
            )}
          </div>
          <span className="text-xs text-content-tertiary truncate">
            {info.row.original.hotelName || info.row.original.emailFrom}
          </span>
        </div>
      ),
    }),
    columnHelper.accessor("category", {
      header: "Category",
      cell: (info) => (
        <span className="text-sm">
          {categoryLabels[info.getValue()] || info.getValue()}
        </span>
      ),
    }),
    columnHelper.accessor("urgency", {
      header: "Urgency",
      cell: (info) => (
        <div
          className={`${urgencyColors[info.getValue()]} text-xs font-medium border rounded py-1 px-2 flex items-center justify-center w-fit capitalize`}
        >
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor("emailSubject", {
      header: "Subject",
      cell: (info) => (
        <div className="max-w-[300px]">
          <p className="text-sm truncate">{info.getValue()}</p>
          {info.row.original.summary && (
            <p className="text-xs text-content-tertiary mt-0.5 line-clamp-2">
              {info.row.original.summary}
            </p>
          )}
        </div>
      ),
    }),
    columnHelper.accessor("createdAt", {
      header: "Created",
      cell: (info) => {
        const date = new Date(info.getValue());
        return (
          <div className="whitespace-nowrap">
            <span className="text-sm text-gray-800 font-medium">
              {formatRelativeDate(date)}
            </span>
            <span className="text-xs text-gray-400 ml-1">
              {date.toLocaleString("en-US", {
                timeZone: "America/New_York",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })}
            </span>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: (info) => (
        <button
          onClick={() =>
            setExpandedId(
              expandedId === info.row.original.id ? null : info.row.original.id,
            )
          }
          className="px-3 py-1.5 text-xs font-medium text-content-secondary bg-surface-secondary rounded hover:bg-gray-200"
        >
          {expandedId === info.row.original.id ? "Close" : "View"}
        </button>
      ),
    }),
  ];

  const table = useReactTable<CsTicket>({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const pendingCount = stats?.pendingReview || 0;
  const todayCount = stats?.today || 0;
  const totalCount = stats?.total || 0;

  return (
    <div className="flex-1">
      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="bg-surface-primary rounded-lg p-4 shadow-sm border border-[#E9E9EB]">
          <div className="text-xs text-content-tertiary mb-1">Pending</div>
          <div className="text-2xl font-semibold text-yellow-600">
            {pendingCount}
          </div>
        </div>
        <div className="bg-surface-primary rounded-lg p-4 shadow-sm border border-[#E9E9EB]">
          <div className="text-xs text-content-tertiary mb-1">Today</div>
          <div className="text-2xl font-semibold text-blue-600">
            {todayCount}
          </div>
        </div>
        <div className="bg-surface-primary rounded-lg p-4 shadow-sm border border-[#E9E9EB]">
          <div className="text-xs text-content-tertiary mb-1">Total</div>
          <div className="text-2xl font-semibold">
            {totalCount}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Search Input */}
        <div className="flex px-3 py-2 border border-[#E9E9EB] bg-surface-primary rounded-lg items-center gap-2 min-w-[240px]">
          <SearchIcon className="w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search tickets..."
            className="w-full outline-none text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Date Preset */}
        <select
          value={datePreset}
          onChange={(e) => {
            const val = e.target.value;
            setDatePreset(val);
            const now = new Date();
            if (val === "today") {
              const start = new Date(now);
              start.setHours(0, 0, 0, 0);
              setDateFrom(start);
              setDateTo(now);
            } else if (val === "7d") {
              const start = new Date(now);
              start.setDate(start.getDate() - 7);
              start.setHours(0, 0, 0, 0);
              setDateFrom(start);
              setDateTo(now);
            } else if (val === "30d") {
              const start = new Date(now);
              start.setDate(start.getDate() - 30);
              start.setHours(0, 0, 0, 0);
              setDateFrom(start);
              setDateTo(now);
            } else if (val === "custom") {
              // keep current dates, user picks manually
            } else {
              setDateFrom(null);
              setDateTo(null);
            }
          }}
          className="px-3 py-2 bg-surface-primary border border-[#E9E9EB] rounded-lg text-sm hover:bg-surface-secondary"
        >
          <option value="">All Time</option>
          <option value="today">Today</option>
          <option value="7d">Past 7 Days</option>
          <option value="30d">Past 30 Days</option>
          <option value="custom">Custom Range</option>
        </select>
        {datePreset === "custom" && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 font-medium">From</span>
            <DatePicker
              selected={dateFrom}
              onChange={(date) => setDateFrom(date)}
              selectsStart
              startDate={dateFrom}
              endDate={dateTo}
              maxDate={dateTo || new Date()}
              placeholderText="Select date"
              dateFormat="MMM dd, yyyy"
              className="px-3 py-2 border border-[#E9E9EB] rounded-lg text-sm bg-surface-primary w-[140px]"
            />
            <span className="text-xs text-gray-400">—</span>
            <span className="text-xs text-gray-500 font-medium">To</span>
            <DatePicker
              selected={dateTo}
              onChange={(date) => setDateTo(date)}
              selectsEnd
              startDate={dateFrom}
              endDate={dateTo}
              minDate={dateFrom}
              maxDate={new Date()}
              placeholderText="Select date"
              dateFormat="MMM dd, yyyy"
              className="px-3 py-2 border border-[#E9E9EB] rounded-lg text-sm bg-surface-primary w-[140px]"
            />
          </div>
        )}

        {/* Status Filter Dropdown */}
        <div className="relative" ref={statusDropdownRef}>
          <button
            onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
            className="flex items-center gap-2 px-3 py-2 bg-surface-primary border border-[#E9E9EB] rounded-lg text-sm hover:bg-surface-secondary"
          >
            <span className="text-content-primary">
              Status:{" "}
              {statusFilter
                ? statusLabels[statusFilter as TicketStatus] || statusFilter
                : "All"}
            </span>
            <ChevronDownIcon
              className={clsx(
                "w-3 h-3 transition-transform",
                statusDropdownOpen && "rotate-180",
              )}
            />
          </button>
          {statusDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 bg-surface-primary border border-[#E9E9EB] rounded-lg shadow-lg z-20 min-w-[160px]">
              {[
                { value: "", label: "All Statuses" },
                { value: "pending_review", label: "Pending Review" },
                { value: "received", label: "Received" },
                { value: "ai_processing", label: "AI Processing" },
                { value: "approved", label: "Approved" },
                { value: "rejected", label: "Rejected" },
                { value: "escalated", label: "Escalated" },
                { value: "failed", label: "Failed" },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setStatusFilter(option.value);
                    setStatusDropdownOpen(false);
                  }}
                  className={clsx(
                    "w-full px-3 py-2 text-left text-sm hover:bg-[#f2f1ff] flex items-center justify-between",
                    option.value === statusFilter && "bg-[#f2f1ff]",
                  )}
                >
                  <span>{option.label}</span>
                  {option.value === statusFilter && (
                    <CheckMarkIcon className="w-4 h-4 text-primary" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Category Filter Dropdown */}
        <div className="relative" ref={categoryDropdownRef}>
          <button
            onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
            className="flex items-center gap-2 px-3 py-2 bg-surface-primary border border-[#E9E9EB] rounded-lg text-sm hover:bg-surface-secondary"
          >
            <span className="text-content-primary">
              Category:{" "}
              {categoryFilter
                ? categoryLabels[categoryFilter as TicketCategory]
                : "All"}
            </span>
            <ChevronDownIcon
              className={clsx(
                "w-3 h-3 transition-transform",
                categoryDropdownOpen && "rotate-180",
              )}
            />
          </button>
          {categoryDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 bg-surface-primary border border-[#E9E9EB] rounded-lg shadow-lg z-20 min-w-[160px]">
              <button
                onClick={() => {
                  setCategoryFilter("");
                  setCategoryDropdownOpen(false);
                }}
                className={clsx(
                  "w-full px-3 py-2 text-left text-sm hover:bg-[#f2f1ff] flex items-center justify-between",
                  categoryFilter === "" && "bg-[#f2f1ff]",
                )}
              >
                <span>All Categories</span>
                {categoryFilter === "" && (
                  <CheckMarkIcon className="w-4 h-4 text-primary" />
                )}
              </button>
              {Object.entries(categoryLabels).map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => {
                    setCategoryFilter(value);
                    setCategoryDropdownOpen(false);
                  }}
                  className={clsx(
                    "w-full px-3 py-2 text-left text-sm hover:bg-[#f2f1ff] flex items-center justify-between",
                    value === categoryFilter && "bg-[#f2f1ff]",
                  )}
                >
                  <span>{label}</span>
                  {value === categoryFilter && (
                    <CheckMarkIcon className="w-4 h-4 text-primary" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Urgency Filter Dropdown */}
        <div className="relative" ref={urgencyDropdownRef}>
          <button
            onClick={() => setUrgencyDropdownOpen(!urgencyDropdownOpen)}
            className="flex items-center gap-2 px-3 py-2 bg-surface-primary border border-[#E9E9EB] rounded-lg text-sm hover:bg-surface-secondary"
          >
            <span className="text-content-primary">
              Urgency:{" "}
              {urgencyFilter
                ? urgencyFilter.charAt(0).toUpperCase() + urgencyFilter.slice(1)
                : "All"}
            </span>
            <ChevronDownIcon
              className={clsx(
                "w-3 h-3 transition-transform",
                urgencyDropdownOpen && "rotate-180",
              )}
            />
          </button>
          {urgencyDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 bg-surface-primary border border-[#E9E9EB] rounded-lg shadow-lg z-20 min-w-[160px]">
              {[
                { value: "", label: "All Urgency" },
                { value: "critical", label: "Critical" },
                { value: "high", label: "High" },
                { value: "medium", label: "Medium" },
                { value: "low", label: "Low" },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setUrgencyFilter(option.value);
                    setUrgencyDropdownOpen(false);
                  }}
                  className={clsx(
                    "w-full px-3 py-2 text-left text-sm hover:bg-[#f2f1ff] flex items-center justify-between",
                    option.value === urgencyFilter && "bg-[#f2f1ff]",
                  )}
                >
                  <span>{option.label}</span>
                  {option.value === urgencyFilter && (
                    <CheckMarkIcon className="w-4 h-4 text-primary" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Department Filter Dropdown */}
        <div className="relative" ref={departmentDropdownRef}>
          <button
            onClick={() => setDepartmentDropdownOpen(!departmentDropdownOpen)}
            className="flex items-center gap-2 px-3 py-2 bg-surface-primary border border-[#E9E9EB] rounded-lg text-sm hover:bg-surface-secondary"
          >
            <span className="text-content-primary">
              Dept:{" "}
              {departmentFilter
                ? departmentLabels[departmentFilter as TicketDepartment]
                : "All"}
            </span>
            <ChevronDownIcon
              className={clsx(
                "w-3 h-3 transition-transform",
                departmentDropdownOpen && "rotate-180",
              )}
            />
          </button>
          {departmentDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 bg-surface-primary border border-[#E9E9EB] rounded-lg shadow-lg z-20 min-w-[160px]">
              {[
                { value: "", label: "All Departments" },
                { value: "customer_success", label: "Customer Success" },
                { value: "engineering", label: "Engineering" },
                { value: "sales", label: "Sales" },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setDepartmentFilter(option.value);
                    setDepartmentDropdownOpen(false);
                  }}
                  className={clsx(
                    "w-full px-3 py-2 text-left text-sm hover:bg-[#f2f1ff] flex items-center justify-between",
                    option.value === departmentFilter && "bg-[#f2f1ff]",
                  )}
                >
                  <span>{option.label}</span>
                  {option.value === departmentFilter && (
                    <CheckMarkIcon className="w-4 h-4 text-primary" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Clear filters */}
        {hasActiveFilters && (
          <button
            onClick={() => {
              setSearch("");
              setStatusFilter("");
              setCategoryFilter("");
              setUrgencyFilter("");
              setDepartmentFilter("");
            }}
            className="text-sm text-content-tertiary hover:text-content-primary underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-surface-primary rounded-lg shadow-sm border border-[#E9E9EB]">
        <div className="overflow-auto max-h-[calc(100vh-280px)] relative flex flex-col">
          <table className="w-full relative">
            <colgroup>
              <col style={{ width: "10%" }} />
              <col style={{ width: "18%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "8%" }} />
              <col style={{ width: "30%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
            </colgroup>
            <thead className="w-full">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="sticky top-0 z-10">
                  {headerGroup.headers.map((header, index) => (
                    <th
                      key={header.id}
                      className={clsx(
                        "text-[0.875rem] font-medium text-[#202020] text-start px-4 py-3 bg-[#F4F4F6]",
                        index === headerGroup.headers.length - 1 &&
                          "rounded-tr-lg",
                        index === 0 && "rounded-tl-lg",
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {!isLoading &&
                table.getRowModel().rows.map((row) => (
                  <React.Fragment key={row.id}>
                    <tr
                      className={clsx(
                        "cursor-pointer hover:bg-surface-secondary",
                        expandedId === row.original.id &&
                          "bg-surface-secondary",
                      )}
                      onClick={() =>
                        setExpandedId(
                          expandedId === row.original.id
                            ? null
                            : row.original.id,
                        )
                      }
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={clsx(
                            "px-4 py-3 border-b border-b-[#D7D7D7] font-medium text-[0.8125rem]",
                            expandedId === row.original.id && "border-b-0",
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </td>
                      ))}
                    </tr>
                    {expandedId === row.original.id && (
                      <tr className="bg-surface-secondary">
                        <td
                          colSpan={7}
                          className="px-4 py-4 border-b border-b-[#D7D7D7]"
                        >
                          <TicketDetail
                            ticket={row.original}
                            onAction={() => {
                              refetch();
                              queryClient.invalidateQueries({
                                queryKey: ["cs-stats"],
                              });
                            }}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              {isFetching && <TableSkeleton rows={3} columns={7} />}
            </tbody>
          </table>

          {!isLoading && !isFetching && tableData.length === 0 && (
            <div className="flex-1 flex flex-col justify-center items-center py-16">
              <span className="text-[#999999] text-[1rem] font-medium">
                No tickets found
              </span>
              <span className="text-[#bbbbbb] text-[0.875rem] mt-1">
                Customer success tickets will appear here
              </span>
            </div>
          )}

          <div>
            <button
              onClick={() => fetchNextPage()}
              disabled={!hasNextPage || isFetchingNextPage}
              ref={loadMoreRef}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// TICKET DETAIL (Expanded Row)
// =============================================================================

function TicketDetail({
  ticket,
  onAction,
}: {
  ticket: CsTicket;
  onAction: () => void;
}) {
  const [editedResponse, setEditedResponse] = useState(
    ticket.finalResponse || "",
  );
  const [showRawAi, setShowRawAi] = useState(false);
  const [responseView, setResponseView] = useState<"preview" | "edit">(
    "preview",
  );
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [copied, setCopied] = useState(false);
  const [customerContext, setCustomerContext] = useState("");
  const [customerContextLoaded, setCustomerContextLoaded] = useState(false);
  const [additionalInstructions, setAdditionalInstructions] = useState("");
  const crmEnabled = useCrmEnabled();

  // Fetch existing customer note for this ticket's sender
  const { data: customerNoteData } = useQuery({
    queryKey: ["customer-note", ticket.id],
    queryFn: () => api.csTickets.getCustomerNote(ticket.id),
  });

  useEffect(() => {
    if (customerNoteData && !customerContextLoaded) {
      setCustomerContext(customerNoteData?.note || "");
      setCustomerContextLoaded(true);
    }
  }, [customerNoteData, customerContextLoaded]);

  const regenerateMutation = useMutation({
    mutationFn: () =>
      api.csTickets.regenerate(
        ticket.id,
        customerContext || undefined,
        additionalInstructions || undefined,
      ),
    onSuccess: (data: CsTicket) => {
      ShowToast({ type: "success", message: "Response regenerated" });
      setEditedResponse(data.finalResponse || "");
      setAdditionalInstructions("");
      onAction();
    },
    onError: () =>
      ShowToast({ type: "error", message: "Failed to regenerate response" }),
  });

  const updateMutation = useMutation({
    mutationFn: () => api.csTickets.updateResponse(ticket.id, editedResponse),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Response updated" });
      onAction();
    },
    onError: () =>
      ShowToast({ type: "error", message: "Failed to update response" }),
  });

  const approveMutation = useMutation({
    mutationFn: () => api.csTickets.approve(ticket.id),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Ticket approved" });
      onAction();
    },
    onError: () =>
      ShowToast({ type: "error", message: "Failed to approve ticket" }),
  });

  const rejectMutation = useMutation({
    mutationFn: () => api.csTickets.reject(ticket.id, rejectReason),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Ticket rejected" });
      setShowRejectInput(false);
      onAction();
    },
    onError: () =>
      ShowToast({ type: "error", message: "Failed to reject ticket" }),
  });

  const [copiedEmail, setCopiedEmail] = useState(false);

  const handleCopyEmail = useCallback(() => {
    const emailMatch = ticket.emailFrom.match(/<(.+?)>/);
    const email = emailMatch ? emailMatch[1] : ticket.emailFrom;
    navigator.clipboard.writeText(email.trim());
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  }, [ticket.emailFrom]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(editedResponse || ticket.finalResponse || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [editedResponse, ticket.finalResponse]);

  const isProcessing =
    ticket.status === "received" ||
    ticket.status === "classifying" ||
    ticket.status === "classified" ||
    ticket.status === "ai_processing";

  const nextSteps = (() => {
    try {
      return ticket.nextSteps ? (JSON.parse(ticket.nextSteps) as string[]) : [];
    } catch {
      return [];
    }
  })();

  const sentimentColor =
    ticket.sentimentTone === "angry" || ticket.sentimentTone === "frustrated"
      ? "bg-red-50 text-red-700 border-red-200"
      : ticket.sentimentTone === "urgent"
        ? "bg-orange-50 text-orange-700 border-orange-200"
        : ticket.sentimentTone === "positive"
          ? "bg-green-50 text-green-700 border-green-200"
          : "bg-surface-secondary text-content-secondary border-border-secondary";

  return (
    <div className="space-y-5" onClick={(e) => e.stopPropagation()}>
      {/* Top bar: customer info + meta badges */}
      <div className="bg-surface-primary rounded-xl border border-border-secondary p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Customer identity */}
          <div className="flex items-center gap-3 min-w-0">
            <div className="shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-bold text-primary">
                {(ticket.customerName || ticket.emailFrom)[0]?.toUpperCase()}
              </span>
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-content-primary truncate">
                  {ticket.customerName || "Unknown"}
                </span>
                {ticket.hotelName && (
                  <span className="text-xs text-gray-400">
                    {ticket.hotelName}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-xs text-content-tertiary truncate">
                  {ticket.emailFrom}
                </span>
                <button
                  onClick={handleCopyEmail}
                  className={`shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded transition-colors ${
                    copiedEmail
                      ? "bg-green-100 text-green-700"
                      : "bg-surface-secondary text-content-tertiary hover:bg-gray-200"
                  }`}
                >
                  {copiedEmail ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          </div>

          {/* Meta badges */}
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`${urgencyColors[ticket.urgency]} text-xs font-medium border rounded-full py-1 px-2.5 capitalize`}
            >
              {ticket.urgency}
            </span>
            <span className="text-xs font-medium bg-surface-secondary text-content-secondary border border-border-secondary rounded-full py-1 px-2.5">
              {categoryLabels[ticket.category]}
            </span>
            <span className="text-xs font-medium bg-surface-secondary text-content-secondary border border-border-secondary rounded-full py-1 px-2.5">
              {departmentLabels[ticket.department]}
            </span>
            {ticket.sentimentTone && (
              <span
                className={`text-xs font-medium border rounded-full py-1 px-2.5 capitalize ${sentimentColor}`}
              >
                {ticket.sentimentTone}
              </span>
            )}
            {ticket.churnRisk && (
              <span className="text-xs font-bold bg-red-100 text-red-700 border border-red-300 rounded-full py-1 px-2.5">
                Churn Risk
              </span>
            )}
            {ticket.needsHumanEscalation && (
              <span className="text-xs font-bold bg-orange-100 text-orange-700 border border-orange-300 rounded-full py-1 px-2.5">
                Needs Escalation
              </span>
            )}
            {crmEnabled && ticket.userId && (
              <Link
                to={`/crm/customers/${ticket.userId}`}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-primary bg-primary/10 border border-primary/20 rounded-lg py-1.5 px-3 hover:bg-primary/20 transition-colors"
              >
                View Customer
              </Link>
            )}
            {ticket.asanaTaskUrl && (
              <a
                href={ticket.asanaTaskUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-white bg-primary rounded-lg py-1.5 px-3 hover:opacity-90 transition-opacity"
              >
                View in Asana
                <span className="text-[10px]">&#8599;</span>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Two-column body */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* ── Left Column ── */}
        <div className="space-y-4">
          {/* Original Email */}
          <div className="bg-surface-primary rounded-xl border border-border-secondary overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="text-xs font-semibold text-content-tertiary uppercase tracking-wider">
                Original Email
              </span>
              <span className="text-xs text-gray-400">
                {new Date(
                  ticket.emailReceivedAt || ticket.createdAt,
                ).toLocaleString("en-US", {
                  timeZone: "America/New_York",
                  month: "short",
                  day: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}
              </span>
            </div>
            <div className="p-4 space-y-2">
              <div className="text-sm font-semibold text-content-primary">
                {ticket.emailSubject}
              </div>
              <div className="text-sm text-content-secondary whitespace-pre-wrap max-h-[220px] overflow-y-auto leading-relaxed">
                {ticket.emailBody}
              </div>
            </div>
          </div>

          {/* Summary */}
          {ticket.summary && (
            <div className="bg-surface-primary rounded-xl border border-border-secondary overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <span className="text-xs font-semibold text-content-tertiary uppercase tracking-wider">
                  AI Summary
                </span>
              </div>
              <div className="p-4">
                <p className="text-sm text-content-primary leading-relaxed">
                  {ticket.summary}
                </p>
              </div>
            </div>
          )}

          {/* Next Steps */}
          {nextSteps.length > 0 && (
            <div className="bg-surface-primary rounded-xl border border-border-secondary overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <span className="text-xs font-semibold text-content-tertiary uppercase tracking-wider">
                  Next Steps for Autumn
                </span>
              </div>
              <div className="p-4">
                <ul className="space-y-2">
                  {nextSteps.map((step, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2.5 text-sm text-content-primary"
                    >
                      <span className="shrink-0 mt-1 w-1.5 h-1.5 rounded-full bg-primary" />
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Classification (collapsible) */}
          {(ticket.extractedQuestion || ticket.failedStep) && (
            <div className="bg-surface-primary rounded-xl border border-border-secondary overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <span className="text-xs font-semibold text-content-tertiary uppercase tracking-wider">
                  Classification Details
                </span>
              </div>
              <div className="p-4 text-sm space-y-2">
                {ticket.extractedQuestion &&
                  ticket.extractedQuestion !== "N/A" && (
                    <div>
                      <span className="text-xs font-medium text-gray-400 uppercase">
                        Extracted Question
                      </span>
                      <p className="text-content-primary mt-0.5">
                        {ticket.extractedQuestion}
                      </p>
                    </div>
                  )}
                {ticket.failedStep && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    <span className="font-medium">Failed at:</span>{" "}
                    {ticket.failedStep} &mdash; {ticket.failedReason}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Raw AI Response (collapsible) */}
          {ticket.aiRawResponse && (
            <div className="bg-surface-primary rounded-xl border border-border-secondary overflow-hidden">
              <button
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-surface-secondary transition-colors"
                onClick={() => setShowRawAi(!showRawAi)}
              >
                <span className="text-xs font-semibold text-content-tertiary uppercase tracking-wider">
                  Raw AI Response
                </span>
                <span
                  className={`text-gray-400 text-xs transition-transform ${showRawAi ? "rotate-180" : ""}`}
                >
                  &#9660;
                </span>
              </button>
              {showRawAi && (
                <div className="px-4 pb-4">
                  <div className="text-sm text-content-secondary whitespace-pre-wrap max-h-[220px] overflow-y-auto leading-relaxed bg-surface-secondary rounded-lg p-3">
                    {ticket.aiRawResponse}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Right Column ── */}
        <div className="space-y-4">
          {/* Final Response */}
          <div className="bg-surface-primary rounded-xl border border-border-secondary overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="text-xs font-semibold text-content-tertiary uppercase tracking-wider">
                Final Response
              </span>
              {!isProcessing && (
                <div className="flex items-center gap-2">
                  <div className="flex bg-gray-100 rounded-full p-0.5">
                    <button
                      onClick={() => setResponseView("preview")}
                      className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                        responseView === "preview"
                          ? "bg-white text-gray-900 shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      Preview
                    </button>
                    <button
                      onClick={() => setResponseView("edit")}
                      className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                        responseView === "edit"
                          ? "bg-white text-gray-900 shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      Edit
                    </button>
                  </div>
                  <button
                    onClick={handleCopy}
                    className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                      copied
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>
              )}
            </div>
            <div className="p-4">
              {isProcessing ? (
                <div className="py-10 text-center">
                  <div className="animate-spin rounded-full h-7 w-7 border-2 border-border-secondary border-t-primary mx-auto mb-3" />
                  <p className="text-sm text-content-tertiary">
                    Processing ticket...
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {statusLabels[ticket.status]}
                  </p>
                </div>
              ) : responseView === "edit" ? (
                <textarea
                  value={editedResponse}
                  onChange={(e) => setEditedResponse(e.target.value)}
                  className="w-full h-[240px] p-3 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-gray-800 leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary focus:bg-surface-primary resize-y transition-colors"
                  placeholder="No response generated yet"
                />
              ) : (
                <div className="h-[240px] overflow-y-auto p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 leading-relaxed prose prose-sm prose-gray max-w-none">
                  {editedResponse ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {editedResponse}
                    </ReactMarkdown>
                  ) : (
                    <span className="text-gray-400">
                      No response generated yet
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Regenerate Section */}
          {!isProcessing && (
            <div className="bg-surface-primary rounded-xl border border-border-secondary overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <span className="text-xs font-semibold text-content-tertiary uppercase tracking-wider">
                  Regenerate
                </span>
              </div>
              <div className="p-4 space-y-4">
                {/* Customer Context */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-content-primary">
                      Customer Context
                    </label>
                    <span className="text-[10px] text-gray-400 font-medium">
                      Saved per customer
                    </span>
                  </div>
                  <textarea
                    value={customerContext}
                    onChange={(e) => setCustomerContext(e.target.value)}
                    className="w-full h-[72px] p-3 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary focus:bg-surface-primary resize-y transition-colors"
                    placeholder="e.g., High-value customer, 45 rooms, comparing to Duetto, been with us 8 months..."
                  />
                </div>

                {/* Additional Instructions */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-content-primary">
                      Additional Instructions
                    </label>
                    <span className="text-[10px] text-gray-400 font-medium">
                      One-time only
                    </span>
                  </div>
                  <textarea
                    value={additionalInstructions}
                    onChange={(e) => setAdditionalInstructions(e.target.value)}
                    className="w-full h-[56px] p-3 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary focus:bg-surface-primary resize-y transition-colors"
                    placeholder="e.g., Do not suggest direct booking, keep it under 3 paragraphs..."
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => regenerateMutation.mutate()}
                    disabled={regenerateMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
                  >
                    {regenerateMutation.isPending
                      ? "Regenerating..."
                      : "Regenerate with AI"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {!isProcessing && (
            <div className="bg-surface-primary rounded-xl border border-border-secondary overflow-hidden">
              <div className="p-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  {editedResponse !== (ticket.finalResponse || "") && (
                    <Button
                      onClick={() => updateMutation.mutate()}
                      loading={updateMutation.isPending}
                    >
                      Save Changes
                    </Button>
                  )}

                  {ticket.status === "pending_review" && (
                    <button
                      onClick={() => approveMutation.mutate()}
                      disabled={approveMutation.isPending}
                      className="px-5 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      {approveMutation.isPending ? "Approving..." : "Approve"}
                    </button>
                  )}

                  {ticket.status === "pending_review" && !showRejectInput && (
                    <button
                      onClick={() => setShowRejectInput(true)}
                      className="px-5 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                    >
                      Reject
                    </button>
                  )}
                </div>

                {showRejectInput && (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Rejection reason..."
                      className="flex-1 px-3 py-2 border border-border-secondary rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                      onClick={() => rejectMutation.mutate()}
                      disabled={rejectMutation.isPending || !rejectReason}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setShowRejectInput(false)}
                      className="px-4 py-2 text-sm font-medium text-content-secondary bg-surface-secondary rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// TABLE SKELETON
// =============================================================================

function TableSkeleton({ rows, columns }: { rows: number; columns: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={`skeleton-row-${rowIndex}`} className="animate-pulse">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <td
              key={`skeleton-cell-${rowIndex}-${colIndex}`}
              className="px-4 py-4 border-b border-b-[#D7D7D7]"
            >
              <div className="w-full h-4 bg-gray-300 rounded-md"></div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
